import { cn } from "@msuite/ui/utils";
import { type Moment, moment } from "@msuite/utils/date";
import type { AbsenceListItem, GroupedAbsenceListItem } from "@msuite/utils/types";
import { memo, useMemo } from "react";
import { AbsencesCalendarBlock } from "./absences-calendar-block";
import { AbsencesCalendarRowWrapper } from "./absences-calendar-row-wrapper";
import { useAbsencesCalendarContext } from "./context";

/** Props Interface */
interface AbsencesCalendarMemberRowProps {
	item: GroupedAbsenceListItem;
}

interface BlockPosition {
	gridColStart: number;
	gridColEnd: number;
	overflowLeft: boolean;
	overflowRight: boolean;
}

function getBlockPosition(
	absence: Omit<AbsenceListItem, "consumedDays">,
	rangeStart: Moment,
	segmentType: "day" | "week",
	actualColumns: number,
): BlockPosition | null {
	const startDiff = moment(absence.startDate).diff(rangeStart, segmentType);
	const endDiff = moment(absence.endDate).diff(rangeStart, segmentType);

	const gridColStart = Math.max(1, startDiff + 1);
	const gridColEnd = Math.min(actualColumns + 1, endDiff + 2);

	if (gridColStart >= gridColEnd) return null;
	return {
		gridColStart,
		gridColEnd,
		overflowLeft: startDiff < 0,
		overflowRight: endDiff + 2 > actualColumns + 1,
	};
}

function AbsencesCalendarMemberRowImpl({ item }: AbsencesCalendarMemberRowProps) {
	/** Context */
	const { controller } = useAbsencesCalendarContext();
	const { cells, columns, actualColumns, segmentType, current, constants } = controller;

	/** Memoized */
	const overlayStyle = useMemo(
		() => ({
			left: constants.FIRST_COLUMN_WIDTH,
			right: 0,
			gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
		}),
		[columns, constants.FIRST_COLUMN_WIDTH],
	);

	const positionedBlocks = useMemo(() => {
		const result: Array<{
			absence: Omit<AbsenceListItem, "consumedDays">;
			pos: BlockPosition;
		}> = [];
		for (const absence of item.items) {
			const pos = getBlockPosition(absence, current, segmentType, actualColumns);
			if (!pos) continue;
			result.push({ absence, pos });
		}
		return result;
	}, [item.items, current, segmentType, actualColumns]);

	/** Render */
	return (
		<div className="w-full relative">
			<AbsencesCalendarRowWrapper variant="content">
				<div className="sticky left-0 right-0">
					<p className="text-sm">{item.userName}</p>
				</div>
				{cells.map((cell, index) => (
					<div
						key={index}
						className={cn(
							"w-full h-full",
							!cell.isInRange && "bg-gray-50 dark:bg-zinc-900/50",
							cell.isInRange && cell.isWeekend && "bg-gray-100 dark:bg-zinc-800",
							cell.isInRange && cell.isHoliday && "stripes",
							cell.isInRange && cell.isToday && "bg-primary-50 dark:bg-primary-950/50",
						)}
					/>
				))}
			</AbsencesCalendarRowWrapper>
			<div
				className="absolute top-0 bottom-0 grid pointer-events-none"
				style={overlayStyle}
			>
				{positionedBlocks.map(({ absence, pos }) => (
					<AbsencesCalendarBlock
						key={absence.absenceId}
						absence={absence}
						gridColStart={pos.gridColStart}
						gridColEnd={pos.gridColEnd}
						overflowLeft={pos.overflowLeft}
						overflowRight={pos.overflowRight}
					/>
				))}
			</div>
		</div>
	);
}

export const AbsencesCalendarMemberRow = memo(AbsencesCalendarMemberRowImpl);
